import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Plus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Seo } from "@/components/brand/Seo";
import {
  BorderBox,
  DarkSection,
  DotList,
  Eyebrow,
  GhostButton,
  Section,
} from "@/components/brand/Bits";
import { CONTACT, CREATOR_POOL } from "@/data/siteContent";

const inputClass =
  "w-full rounded-xl border border-[#D9D3C7] bg-white px-3.5 py-2.5 text-[15px] text-[#111111] outline-none transition-colors focus:border-[#F2811D]";

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-[#111111]">
        {label}
        {required && <span className="ml-1 text-[#C2410C]">*</span>}
      </span>
      {hint && <span className="mt-0.5 block text-xs body-text">{hint}</span>}
      <div className="mt-2">{children}</div>
    </label>
  );
}

export default function JoinCreators() {
  const [options, setOptions] = useState({ categories: [], platforms: [] });
  const [socials, setSocials] = useState([{ platform: "Instagram", handle: "", followers: "" }]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    display_name: "",
    email: "",
    phone: "",
    city: "",
    audience_note: "",
    rate_note: "",
    portfolio_url: "",
    note: "",
    company: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get("/public/creator-application-options")
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions({ categories: [], platforms: ["Instagram", "XiaoHongShu", "TikTok"] }));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleCategory = (cat) =>
    setCategories((list) => (list.includes(cat) ? list.filter((c) => c !== cat) : [...list, cat]));

  const setSocial = (index, key, value) =>
    setSocials((list) => list.map((s, i) => (i === index ? { ...s, [key]: value } : s)));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (categories.length === 0) {
      setError("Select at least one content category.");
      return;
    }
    if (!socials.some((s) => s.handle.trim())) {
      setError("Add at least one social platform and handle.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/creator-applications", {
        ...form,
        categories,
        socials: socials
          .filter((s) => s.handle.trim())
          .map((s) => ({
            platform: s.platform,
            handle: s.handle.trim(),
            followers: s.followers === "" ? null : Number(s.followers),
          })),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        path="/join"
        title="Join the AnyTribe Creator Pool — Apply as a KOL or Creator"
        description="Apply to join AnyTribe's creator pool. We work with nano to premium creators across beauty, skincare, wellness, personal care, lifestyle, food and parenting in Malaysia."
      />

      <DarkSection testId="join-hero" className="pt-20 sm:pt-24">
        <div className="max-w-3xl">
          <Eyebrow dark>For creators & KOLs</Eyebrow>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
            Join the AnyTribe creator pool
          </h1>
          <p className="mt-6 text-base leading-relaxed text-white/75 md:text-lg">
            We work with creators from nano to premium — and we lead with the lower tiers, because
            that's where trust, efficiency and iteration speed live. Tell us about your audience and
            we'll match you to campaigns your followers will actually care about.
          </p>
        </div>
        <div className="mt-12 grid gap-4 border-t border-white/12 pt-10 sm:grid-cols-3">
          <BorderBox dark testId="join-benefit-1">
            <h2 className="font-heading text-base font-extrabold text-white">Briefed properly</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Clear hooks, angles and formats — the brief specifies the job, not just the mention.
            </p>
          </BorderBox>
          <BorderBox dark testId="join-benefit-2">
            <h2 className="font-heading text-base font-extrabold text-white">Your voice protected</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              We protect the voice that earned the audience. No scripts that don't sound like you.
            </p>
          </BorderBox>
          <BorderBox dark testId="join-benefit-3">
            <h2 className="font-heading text-base font-extrabold text-white">One place to submit</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Approved creators get a portal to submit posting links, insights and invoices from
              their phone.
            </p>
          </BorderBox>
        </div>
      </DarkSection>

      <Section testId="join-form-section">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
          <div>
            {done ? (
              <div
                className="rounded-2xl border border-[#BBE0C2] bg-[#F1F9F2] p-7"
                data-testid="join-success"
              >
                <CheckCircle2 className="h-8 w-8 text-[#2F7D3B]" />
                <h2 className="mt-4 font-heading text-2xl font-extrabold">Application received</h2>
                <p className="mt-3 text-[15px] leading-relaxed body-text">
                  Thanks — your application is with the AnyTribe team. We review every application
                  and get in touch when there's a campaign that fits your audience. If you're
                  approved you'll receive an activation link by email for your creator portal.
                </p>
                <p className="mt-4 text-sm body-text">
                  Questions in the meantime? Email{" "}
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="font-bold text-[#F2811D] hover:underline"
                  >
                    {CONTACT.email}
                  </a>
                  .
                </p>
                <div className="mt-6">
                  <GhostButton to="/services" testId="join-success-services">
                    See what we do
                  </GhostButton>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="join-form" noValidate>
                <div>
                  <Eyebrow>Apply</Eyebrow>
                  <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight">
                    Tell us about your audience
                  </h2>
                  <p className="mt-3 text-[15px] leading-relaxed body-text">
                    Takes about two minutes. Nothing here is published publicly — it goes straight
                    to the AnyTribe team.
                  </p>
                </div>

                {error && (
                  <p
                    className="rounded-xl border border-[#F3C2B4] bg-[#FDF1ED] px-4 py-3 text-sm font-semibold text-[#9C3418]"
                    data-testid="join-error"
                  >
                    {error}
                  </p>
                )}

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Name or creator handle" required>
                    <input
                      required
                      value={form.display_name}
                      onChange={set("display_name")}
                      className={inputClass}
                      data-testid="join-name-input"
                    />
                  </Field>
                  <Field label="Email" required>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputClass}
                      data-testid="join-email-input"
                    />
                  </Field>
                  <Field label="Phone / WhatsApp">
                    <input
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputClass}
                      data-testid="join-phone-input"
                    />
                  </Field>
                  <Field label="City">
                    <input
                      value={form.city}
                      onChange={set("city")}
                      className={inputClass}
                      data-testid="join-city-input"
                    />
                  </Field>
                </div>

                <div>
                  <p className="text-sm font-bold text-[#111111]">
                    Social platforms <span className="text-[#C2410C]">*</span>
                  </p>
                  <p className="mt-0.5 text-xs body-text">
                    Add every platform you post on. Follower counts are optional.
                  </p>
                  <div className="mt-3 space-y-3" data-testid="join-socials">
                    {socials.map((social, index) => (
                      <div key={index} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                        <select
                          value={social.platform}
                          onChange={(e) => setSocial(index, "platform", e.target.value)}
                          className={`${inputClass} sm:w-[9.5rem]`}
                          data-testid={`join-social-platform-${index}`}
                        >
                          {(options.platforms || []).map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                        <input
                          placeholder="@handle"
                          value={social.handle}
                          onChange={(e) => setSocial(index, "handle", e.target.value)}
                          className={`${inputClass} min-w-0 flex-1`}
                          data-testid={`join-social-handle-${index}`}
                        />
                        <input
                          type="number"
                          min="0"
                          placeholder="Followers"
                          value={social.followers}
                          onChange={(e) => setSocial(index, "followers", e.target.value)}
                          className={`${inputClass} sm:w-[8.5rem]`}
                          data-testid={`join-social-followers-${index}`}
                        />
                        {socials.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setSocials((l) => l.filter((_, i) => i !== index))}
                            aria-label="Remove platform"
                            className="rounded-full border border-[#D9D3C7] p-2 text-[#6b6660] hover:border-[#111111]"
                            data-testid={`join-social-remove-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSocials((l) => [...l, { platform: "TikTok", handle: "", followers: "" }])
                    }
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#F2811D] hover:underline"
                    data-testid="join-add-social"
                  >
                    <Plus className="h-4 w-4" /> Add another platform
                  </button>
                </div>

                <div>
                  <p className="text-sm font-bold text-[#111111]">
                    Content categories <span className="text-[#C2410C]">*</span>
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="join-categories">
                    {(options.categories || []).map((cat) => {
                      const active = categories.includes(cat);
                      return (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => toggleCategory(cat)}
                          data-testid={`join-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors ${
                            active
                              ? "border-[#F2811D] bg-[#F2811D] text-white"
                              : "border-[#D9D3C7] bg-white text-[#111111] hover:border-[#111111]"
                          }`}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Field
                  label="Who is your audience?"
                  hint="Age, language, location — whatever helps us match you to the right brands."
                >
                  <textarea
                    rows={3}
                    value={form.audience_note}
                    onChange={set("audience_note")}
                    className={inputClass}
                    data-testid="join-audience-input"
                  />
                </Field>

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Indicative rates" hint="Optional. In RM, per deliverable.">
                    <input
                      value={form.rate_note}
                      onChange={set("rate_note")}
                      placeholder="e.g. RM 800 per TikTok video"
                      className={inputClass}
                      data-testid="join-rates-input"
                    />
                  </Field>
                  <Field label="Portfolio or media kit link" hint="Optional. Must start with https://">
                    <input
                      value={form.portfolio_url}
                      onChange={set("portfolio_url")}
                      placeholder="https://"
                      className={inputClass}
                      data-testid="join-portfolio-input"
                    />
                  </Field>
                </div>

                <Field label="Anything else?">
                  <textarea
                    rows={3}
                    value={form.note}
                    onChange={set("note")}
                    className={inputClass}
                    data-testid="join-note-input"
                  />
                </Field>

                {/* honeypot — hidden from real users */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={set("company")}
                  className="hidden"
                  aria-hidden="true"
                />

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="join-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F2811D] px-7 py-3 text-[15px] font-bold text-white transition-colors hover:bg-[#D96C0C] disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? "Sending…" : "Submit application"}
                </button>
                <p className="text-xs leading-relaxed body-text">
                  By applying you agree that AnyTribe may contact you about campaign opportunities.
                  We don't publish your details or share them outside campaign briefing.
                </p>
              </form>
            )}
          </div>

          <aside className="space-y-4 self-start">
            <BorderBox testId="join-aside-categories">
              <h2 className="font-heading text-lg font-extrabold">Categories we brief most</h2>
              <ul className="mt-4 flex flex-wrap gap-2">
                {CREATOR_POOL.categories.map((cat) => (
                  <li
                    key={cat}
                    className="rounded-full border border-[#E6E1D8] bg-[#F7F4EF] px-3 py-1 text-sm font-bold"
                  >
                    {cat}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm leading-relaxed body-text">{CREATOR_POOL.audiences}</p>
            </BorderBox>

            <BorderBox testId="join-aside-tiers">
              <h2 className="font-heading text-lg font-extrabold">Follower count isn't the filter</h2>
              <p className="mt-4 text-sm leading-relaxed body-text">
                We brief nano and micro creators as often as larger accounts — they're where trust,
                efficiency and iteration speed live. Apply whatever your following, and tell us who
                actually watches you.
              </p>
            </BorderBox>

            <BorderBox testId="join-aside-what-happens">
              <h2 className="font-heading text-lg font-extrabold">What happens next</h2>
              <DotList
                className="mt-4"
                items={[
                  "The AnyTribe team reviews your application.",
                  "If there's a fit, we reach out about a specific campaign.",
                  "Approved creators get an activation link for their own portal.",
                ]}
              />
            </BorderBox>
          </aside>
        </div>
      </Section>
    </>
  );
}
